import { useContext, useEffect, useState } from 'react';

import {
  BookLocationDocumentValuesContext,
  DocumentValuesContext,
} from 'contexts';
import { getLocalSettingsProxy } from 'utilities';
/**
 * The base path logic is an annoying tech debt thing.
 *
 * In older books (processed before ~2019)
 * the base url of epub content in the iframe is /rscontent/{epubid}
 * but in newer books the base url is /rscontent/{epubid}/{processingreportid}
 *
 * Without being able to rely on a consistent structure,
 * the only way to get the base url (needed for offline) is to read the base tag
 * of the html within the iframe, and parse out the relative part of the path.
 *
 * The relative part of the path will be the path of the html within the source epub.
 * So if the base tag for the current document is /rscontent/1/23/OEBPS/html/1.html
 * and the document's path within the source epub is /OEBPS/html/1.html
 * then the base path for the epub is /rscontent/1/23/
 *
 * Many epubs use /OEBPS or similar structure, but nothing is stopping it from
 * using a number, so we need the document epub html path to figure it out.
 *
 * It's not a part of the skeleton because we weren't supposed to need it.
 *
 * @method useBookContentBasePath
 * @returns {String} the base path for all book content, or null if the document has not yet loaded
 */
export default function useBookContentBasePath() {
  const { currentBookLocationDocument } = useContext(
    BookLocationDocumentValuesContext,
  );
  const { frameDocument } = useContext(DocumentValuesContext);
  const [localSettings] = useState(() =>
    getLocalSettingsProxy(currentBookLocationDocument.book.pk),
  );
  const [basePath, setBasePath] = useState(localSettings.bookContentBasePath);
  const [basePathUpdatedFromDocumentOnce, setBasePathUpdatedFromDocumentOnce] =
    useState(false);

  useEffect(() => {
    if (
      !frameDocument ||
      !currentBookLocationDocument ||
      basePathUpdatedFromDocumentOnce
    ) {
      return;
    }

    const relativePath = currentBookLocationDocument.path;

    if (!relativePath) {
      throw new Error('Missing document relative epub path');
    }

    const fullURL = frameDocument.head?.getElementsByTagName('base')[0]?.href;

    if (!fullURL) {
      return;
    }

    const fullPath = new URL(fullURL).pathname;

    setBasePath(fullPath.split(relativePath)[0]);
    setBasePathUpdatedFromDocumentOnce(true);
  }, [
    basePathUpdatedFromDocumentOnce,
    currentBookLocationDocument,
    frameDocument,
  ]);

  // Store in localSettings so that it is available offline, even if the user is on an unavailable document.
  localSettings.bookContentBasePath = basePath;

  // Once the base path is determined, it never changes.
  return basePath;
}
